// autocomplete.ts — CompletionSources for the editor's autocomplete popup.
//
// Four trigger modes share the CM6 @codemirror/autocomplete tooltip:
//   • [[…]]   — wiki-link to a note name (existing or referenced-but-missing)
//   • [text]( — markdown link to a note name; underscore-encodes spaces
//   • /cmd    — slash-command palette inserting markdown skeletons
//   • # title — new-note title hint when the document has no name yet
//
// Each mode is implemented as a CompletionSource and registered with
// NoteEditor.registerCompletionSource(). The shim wires a single
// autocompletion() extension that walks the registry; the popup, keyboard
// handling, positioning, and ARIA wiring all come from CM6 — no homegrown
// dropdown DOM, no mirror-textarea measurement.
//
// References shared globals: window.NoteStorage, CALENDARS_NOTE,
// currentFileName, loadNote.

import { CALENDARS_NOTE, LABYRINTH_NOTE, getCurrentFileName } from '../core/app-state.js';
import { AttachmentPicker } from '../notes/attachment-picker.js';
import { loadNote } from '../notes/note-actions.js';
import { linkTarget } from '../notes/note-graph-index.js';
import { candidateNoteNames, installNoteNameCacheInvalidation } from '../notes/note-name-source.js';
import { addDays, getFormattedDate, nextWeekday, nowTimeRange, toYYMMDD } from '../utils/dates.js';
// Single-sourced math vocabulary (#20): the LaTeX command list + column reducers
// come straight from the zero-import math-expr leaf, so the completion menu can't
// drift from what the evaluator accepts and no import cycle forms.
import { MATH_LATEX_COMMANDS, VEC_FN_NAMES } from '../utils/math-expr.js';
import { isScheduledLine } from '../utils/search-predicate.js';
// Shared math-context scanner (#24) — the SAME implementation the Tab field-nav
// and `=`-open use, so "am I in math?" agrees across all three. Zero-import
// pure leaf, no cycle.
import { mathContextAtCaret } from './math-fields.js';
import { NoteEditor } from './note-editor.js';
import { insertEvent } from './schedule-insert.js';

(() => {
  if (!NoteEditor?.registerCompletionSource) {
    console.error('autocomplete.js: NoteEditor not ready');
    return;
  }

  // ── Slash-command catalogue ──────────────────────────────────────────────
  // `insert` may contain a `‸` (U+2038) sentinel marking the desired final
  // caret position; it's stripped before reaching the document. `|` cannot be
  // a sentinel because table templates need literal pipes.
  // Today's date as YYMMDD — the shared helper (== toYYMMDD(new Date())).
  function _slashToday(): string {
    return getFormattedDate();
  }
  // Current time as "HHMM" (no rounding — an exact timestamp for /now).
  function _slashNow(): string {
    const d = new Date();
    return String(d.getHours()).padStart(2, '0') + String(d.getMinutes()).padStart(2, '0');
  }
  // Wrap a diagram body in a ```mermaid fence; the caret (‸) lands on the line
  // after the block so the diagram renders immediately and the user can keep
  // typing (or click into the block to edit the template).
  function _mm(body: string): string {
    return `\`\`\`mermaid\n${body}\n\`\`\`\n‸`;
  }

  // Canonical schedule tail: "> YYMMDD [HHMM [HHMM]]" anchored at the line end.
  // The two time groups are optional, so an all-day tail parses with
  // startTime/endTime undefined — the "is this line already a (timed?) schedule
  // line" test used by the context-aware /today · /now applies.
  const SCHED_TAIL_RE = /(?:^|\s)>\s(\d{6})(?:\s(\d{4}))?(?:\s(\d{4}))?\s*$/;

  // Caret context for a slash apply: the line, the text before the "/cmd" and
  // after the caret, and derived flags. `rest` is the line with the "/cmd"
  // token removed (before + after) — what the line becomes once the command
  // consumes its trigger. Shared by the date/now/event/footnote applies.
  function _slashCtx(view: any, from: number, to: number): any {
    const doc = view.state.doc;
    const line = doc.lineAt(from);
    const before = doc.sliceString(line.from, from);
    const after = doc.sliceString(to, line.to);
    const rest = before + after;
    const m = SCHED_TAIL_RE.exec(rest);
    return {
      line,
      before,
      after,
      rest,
      atLineStart: /^\s*$/.test(before),
      endOfLine: /^\s*$/.test(after),
      empty: rest.trim() === '',
      scheduleTail: m ? { date: m[1], startTime: m[2], endTime: m[3] } : null,
      // Canonical "is this already a schedule line" — recognises tails that
      // SCHED_TAIL_RE misses (e.g. a trailing `@cal` / `~rrule` tag), so we never
      // append a SECOND `> date` and corrupt the grammar into `> date @tag > date`.
      alreadyScheduled: isScheduledLine(rest),
    };
  }

  // /today (and every relative-date command) apply. At the END of a non-empty,
  // not-already-scheduled line, PROMOTE the line to an all-day schedule item by
  // appending " > YYMMDD" — so the user never types the ">" first — and offer a
  // Tab-to-accept timed range as a follow-up ghost. Elsewhere (empty line,
  // mid-sentence, or a line that already carries a schedule tail) insert a plain
  // bare date that reads naturally in prose.
  function _applyAllDayDate(view: any, from: number, to: number, yymmdd: string): void {
    const ctx = _slashCtx(view, from, to);
    if (ctx.endOfLine && !ctx.empty && !ctx.scheduleTail && !ctx.alreadyScheduled) {
      const head = ctx.before.replace(/\s*$/, '');
      const newText = `${head} > ${yymmdd}${ctx.after}`;
      const caret = ctx.line.from + head.length + 3 + yymmdd.length; // after "<head> > YYMMDD"
      view.dispatch({
        changes: { from: ctx.line.from, to: ctx.line.to, insert: newText },
        selection: { anchor: caret },
      });
      if (typeof NoteEditor.seedGhost === 'function') {
        queueMicrotask(() => NoteEditor.seedGhost(` ${nowTimeRange()}`));
      }
      return;
    }
    view.dispatch({
      changes: { from, to, insert: `${yymmdd} ` },
      selection: { anchor: from + yymmdd.length + 1 },
    });
  }

  // /now apply. Upgrade a line already ending in an all-day "> YYMMDD" (no times
  // yet) to a timed range in place; otherwise insert a bare "HHMM ".
  function _applyNow(view: any, from: number, to: number): void {
    const ctx = _slashCtx(view, from, to);
    if (ctx.scheduleTail && !ctx.scheduleTail.startTime) {
      const upgraded = `${ctx.rest.replace(/\s*$/, '')} ${nowTimeRange()}`;
      view.dispatch({
        changes: { from: ctx.line.from, to: ctx.line.to, insert: upgraded },
        selection: { anchor: ctx.line.from + upgraded.length },
      });
      return;
    }
    const hhmm = _slashNow();
    view.dispatch({
      changes: { from, to, insert: `${hhmm} ` },
      selection: { anchor: from + hhmm.length + 1 },
    });
  }

  // /footnote apply. Insert an auto-numbered [^n] ref at the caret and a matching
  // "[^n]: " definition stub at doc-end (the editor's relocate listener moves it
  // into the footnote section), landing the caret inside the definition.
  function _applyFootnote(view: any, from: number, to: number): void {
    const id = NoteEditor.getNextFootnoteId();
    const ref = `[^${id}]`;
    const docEnd = view.state.doc.length;
    const needsNL = docEnd > 0 && view.state.doc.sliceString(docEnd - 1) !== '\n';
    const defStub = `${needsNL ? '\n' : ''}[^${id}]: `;
    const delta = ref.length - (to - from);
    view.dispatch({
      changes: [
        { from, to, insert: ref },
        { from: docEnd, insert: defStub },
      ],
      selection: { anchor: docEnd + delta + defStub.length },
    });
  }

  // ── Typo-tolerant fuzzy fallback (only when the strict pass finds nothing) ──
  function _isSubsequence(short: string, long: string): boolean {
    let i = 0;
    for (let j = 0; j < long.length && i < short.length; j++) {
      if (short[i] === long[j]) i++;
    }
    return i === short.length;
  }
  // Bounded Levenshtein: early-exits with 3 as soon as the best edit distance on
  // a row exceeds 2, so it's cheap over the whole catalogue per keystroke.
  function _levenshtein(a: string, b: string): number {
    const m = a.length;
    const n = b.length;
    if (Math.abs(m - n) > 2) return 3;
    let prev = Array.from({ length: n + 1 }, (_, j) => j);
    for (let i = 1; i <= m; i++) {
      const cur = [i];
      let rowMin = i;
      for (let j = 1; j <= n; j++) {
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
        if (cur[j] < rowMin) rowMin = cur[j];
      }
      if (rowMin > 2) return 3;
      prev = cur;
    }
    return prev[n];
  }
  function _fuzzyCmd(partial: string, cmd: SlashCommand): boolean {
    const tokens = [
      cmd.name,
      ...(cmd.keywords || '').split(/\s+/),
      cmd.label.toLowerCase().split(/\s+/)[0],
    ];
    return tokens.some(
      (t) =>
        !!t &&
        (_isSubsequence(partial, t) ||
          (Math.abs(t.length - partial.length) <= 2 && _levenshtein(partial, t) <= 2))
    );
  }

  interface SlashCommand {
    name: string;
    label: string;
    hint: string;
    // Most commands carry a static `insert` (a `‸`-sentinel template). A few
    // alternatives:
    //   • `build()` returns the insert text fresh at apply time (so /today,
    //     /now reflect the current date/time, not module-load time);
    //   • `snippet` is a CM6 snippet template with ${field} tab-stops
    //     (e.g. /link → type text, Tab to the URL).
    insert?: string;
    build?: () => string;
    snippet?: string;
    // `inline: true` commands also fire mid-line (after a space), not just at
    // the start of a line — for things that read naturally inside a sentence
    // (inline math, today/now, links).
    inline?: boolean;
    keywords?: string;
    mermaid?: boolean;
    seedDateGhost?: boolean;
    opensTableSkeleton?: boolean;
    opensFormulaSkeleton?: boolean;
    opensFilePicker?: boolean;
    // Context-aware date commands: `allday` (/today, /tomorrow, weekdays…) and
    // `now` (/now). `dateValue` computes the YYMMDD fresh at apply time.
    kind?: 'allday' | 'now';
    dateValue?: () => string;
    // Appends a timed-event stamp at the line end via the shared insertEvent leaf.
    insertsEvent?: boolean;
    // Inserts an auto-numbered footnote ref + definition stub.
    insertsFootnote?: boolean;
    // A non-inserting parent that re-opens the palette scoped to the Mermaid
    // subtypes (keeps the bare `/` list short).
    expandMermaid?: boolean;
  }
  // Every Mermaid v11 diagram type, each inserting a ready-to-edit template.
  // The parent `mermaid` entry (a flowchart) shows in the bare `/` list; the
  // rest are tagged `mermaid:true` and surface once the user types — `/mermaid`
  // lists them all (their labels contain "mermaid"), `/seq` etc. jump to one.
  const _MERMAID_COMMANDS: SlashCommand[] = [
    // Non-inserting parent shown in the bare `/` list; selecting it (or typing a
    // prefix of "mermaid") re-opens the palette listing every subtype — so the
    // 20+ diagram types don't crowd the bare list.
    {
      name: 'mermaid',
      label: 'Mermaid diagram…',
      hint: '◇ 22 types',
      keywords: 'diagram graph chart',
      expandMermaid: true,
    },
    {
      name: 'flowchart',
      label: 'Mermaid · Flowchart',
      hint: '◇ flow',
      keywords: 'mermaid diagram graph flowchart',
      mermaid: true,
      insert: _mm(
        'flowchart TD\n  A[Start] --> B{Decision}\n  B -->|Yes| C[OK]\n  B -->|No| D[Stop]'
      ),
    },
    {
      name: 'sequence',
      label: 'Mermaid · Sequence',
      hint: '⇄ seq',
      keywords: 'mermaid interaction message',
      mermaid: true,
      insert: _mm(
        'sequenceDiagram\n  participant A as Alice\n  participant B as Bob\n  A->>B: Hello Bob\n  B-->>A: Hi Alice'
      ),
    },
    {
      name: 'class',
      label: 'Mermaid · Class',
      hint: '▭ OO',
      keywords: 'mermaid uml object',
      mermaid: true,
      insert: _mm(
        'classDiagram\n  class Animal {\n    +String name\n    +makeSound()\n  }\n  Animal <|-- Dog'
      ),
    },
    {
      name: 'state',
      label: 'Mermaid · State',
      hint: '◉ machine',
      keywords: 'mermaid statechart transition',
      mermaid: true,
      insert: _mm(
        'stateDiagram-v2\n  [*] --> Idle\n  Idle --> Running : start\n  Running --> Idle : stop\n  Running --> [*]'
      ),
    },
    {
      name: 'er',
      label: 'Mermaid · ER',
      hint: '⊞ entities',
      keywords: 'mermaid entity relationship database',
      mermaid: true,
      insert: _mm(
        'erDiagram\n  CUSTOMER ||--o{ ORDER : places\n  ORDER ||--|{ LINE_ITEM : contains'
      ),
    },
    {
      name: 'gantt',
      label: 'Mermaid · Gantt',
      hint: '▤ schedule',
      keywords: 'mermaid timeline project plan',
      mermaid: true,
      insert: _mm(
        'gantt\n  title Project Plan\n  dateFormat YYMMDD\n  axisFormat %y%m%d\n  tickInterval 1week\n  section Phase 1\n  Task A :a1, 260101, 7d\n  Task B :after a1, 5d'
      ),
    },
    {
      name: 'pie',
      label: 'Mermaid · Pie',
      hint: '◔ chart',
      keywords: 'mermaid proportion percentage',
      mermaid: true,
      insert: _mm('pie title Pets\n  "Dogs" : 40\n  "Cats" : 35\n  "Birds" : 25'),
    },
    {
      name: 'journey',
      label: 'Mermaid · Journey',
      hint: '☺ user',
      keywords: 'mermaid experience steps',
      mermaid: true,
      insert: _mm(
        'journey\n  title My Day\n  section Morning\n    Wake up: 3: Me\n    Coffee: 5: Me'
      ),
    },
    {
      name: 'gitgraph',
      label: 'Mermaid · Git graph',
      hint: '⑂ commits',
      keywords: 'mermaid git branch commit',
      mermaid: true,
      insert: _mm(
        'gitGraph\n  commit\n  branch develop\n  checkout develop\n  commit\n  checkout main\n  merge develop'
      ),
    },
    {
      name: 'mindmap',
      label: 'Mermaid · Mindmap',
      hint: '✸ ideas',
      keywords: 'mermaid brainstorm tree',
      mermaid: true,
      insert: _mm(
        'mindmap\n  root((Idea))\n    Origins\n      History\n    Research\n      Effectiveness'
      ),
    },
    {
      name: 'timeline',
      label: 'Mermaid · Timeline',
      hint: '▬ events',
      keywords: 'mermaid history chronology',
      mermaid: true,
      insert: _mm(
        'timeline\n  title History\n  2021 : Founded\n  2022 : Series A\n  2023 : Launch'
      ),
    },
    {
      name: 'quadrant',
      label: 'Mermaid · Quadrant',
      hint: '田 matrix',
      keywords: 'mermaid chart scatter priority',
      mermaid: true,
      insert: _mm(
        'quadrantChart\n  title Reach vs Effort\n  x-axis Low Effort --> High Effort\n  y-axis Low Reach --> High Reach\n  "A": [0.3, 0.6]\n  "B": [0.45, 0.23]'
      ),
    },
    {
      name: 'requirement',
      label: 'Mermaid · Requirement',
      hint: '✓ specs',
      keywords: 'mermaid requirements verify',
      mermaid: true,
      insert: _mm(
        'requirementDiagram\n  requirement test_req {\n    id: 1\n    text: the test text\n    risk: high\n    verifymethod: test\n  }\n  element test_entity {\n    type: simulation\n  }\n  test_entity - satisfies -> test_req'
      ),
    },
    {
      name: 'c4',
      label: 'Mermaid · C4 context',
      hint: '▦ arch',
      keywords: 'mermaid architecture system context',
      mermaid: true,
      insert: _mm(
        'C4Context\n  title System Context\n  Person(user, "User")\n  System(sys, "My System")\n  Rel(user, sys, "Uses")'
      ),
    },
    {
      name: 'sankey',
      label: 'Mermaid · Sankey',
      hint: '⇶ flow',
      keywords: 'mermaid flow energy',
      mermaid: true,
      insert: _mm(
        'sankey-beta\n  Agricultural,Bio-conversion,124\n  Bio-conversion,Losses,26\n  Bio-conversion,Solid,280'
      ),
    },
    {
      name: 'xychart',
      label: 'Mermaid · XY chart',
      hint: '📈 bar/line',
      keywords: 'mermaid chart bar line axis',
      mermaid: true,
      insert: _mm(
        'xychart-beta\n  title "Revenue"\n  x-axis [jan, feb, mar, apr]\n  y-axis "Amount" 0 --> 100\n  bar [30, 50, 40, 70]\n  line [30, 50, 40, 70]'
      ),
    },
    {
      name: 'block',
      label: 'Mermaid · Block',
      hint: '▣ blocks',
      keywords: 'mermaid block layout',
      mermaid: true,
      insert: _mm('block-beta\n  columns 3\n  a b c\n  d["wide"]:2 e'),
    },
    {
      name: 'packet',
      label: 'Mermaid · Packet',
      hint: '⌗ bytes',
      keywords: 'mermaid network protocol byte',
      mermaid: true,
      insert: _mm(
        'packet-beta\n  title TCP Header\n  0-15: "Source Port"\n  16-31: "Destination Port"\n  32-63: "Sequence Number"'
      ),
    },
    {
      name: 'kanban',
      label: 'Mermaid · Kanban',
      hint: '▥ board',
      keywords: 'mermaid board todo tasks',
      mermaid: true,
      insert: _mm(
        'kanban\n  Todo\n    [Create issue]\n    [Fix bug]\n  In Progress\n    [Write docs]\n  Done\n    [Ship it]'
      ),
    },
    {
      name: 'architecture',
      label: 'Mermaid · Architecture',
      hint: '☁ services',
      keywords: 'mermaid architecture cloud infra',
      mermaid: true,
      insert: _mm(
        'architecture-beta\n  group api(cloud)[API]\n  service db(database)[Database] in api\n  service server(server)[Server] in api\n  db:L -- R:server'
      ),
    },
    {
      name: 'radar',
      label: 'Mermaid · Radar',
      hint: '◈ spider',
      keywords: 'mermaid radar spider skills',
      mermaid: true,
      insert: _mm(
        'radar-beta\n  title Skills\n  axis speed, power, range, focus\n  curve hero{80, 60, 40, 70}\n  max 100\n  min 0'
      ),
    },
    {
      name: 'treemap',
      label: 'Mermaid · Treemap',
      hint: '▦ nested',
      keywords: 'mermaid treemap hierarchy nested',
      mermaid: true,
      insert: _mm(
        'treemap-beta\n"Root"\n  "Branch A"\n    "Leaf 1": 10\n    "Leaf 2": 20\n  "Branch B"\n    "Leaf 3": 30'
      ),
    },
  ];
  // Relative-date commands. All share `kind:'allday'` so they route through
  // _applyAllDayDate (end-of-line promotion + empty-line bare date). `dateValue`
  // computes the concrete YYMMDD fresh at apply time.
  const _RELATIVE_DATE_COMMANDS: SlashCommand[] = [
    {
      name: 'tomorrow',
      label: 'Tomorrow',
      hint: 'YYMMDD',
      kind: 'allday',
      dateValue: () => toYYMMDD(addDays(new Date(), 1)),
      inline: true,
      keywords: 'date next',
    },
    {
      name: 'yesterday',
      label: 'Yesterday',
      hint: 'YYMMDD',
      kind: 'allday',
      dateValue: () => toYYMMDD(addDays(new Date(), -1)),
      inline: true,
      keywords: 'date prev previous',
    },
    {
      name: 'nextweek',
      label: 'Next week',
      hint: 'YYMMDD',
      kind: 'allday',
      // Literal +7 days from today — intentionally DIFFERENT from the NL phrase
      // "next week" (Monday of next ISO week) and from /monday (which gives that
      // Monday). Uses new Date() at apply time, so no unit test covers it.
      dateValue: () => toYYMMDD(addDays(new Date(), 7)),
      inline: true,
      keywords: 'date week',
    },
    // The seven weekdays resolve to the next strictly-future occurrence (so
    // /monday on a Monday means *next* Monday). Short forms (/mon, /tue…) match
    // via the keyword aliases.
    ...(
      [
        ['monday', 1, 'mon'],
        ['tuesday', 2, 'tue'],
        ['wednesday', 3, 'wed'],
        ['thursday', 4, 'thu'],
        ['friday', 5, 'fri'],
        ['saturday', 6, 'sat'],
        ['sunday', 0, 'sun'],
      ] as Array<[string, number, string]>
    ).map(([name, dow, short]) => ({
      name,
      label: `Next ${name[0].toUpperCase()}${name.slice(1)}`,
      hint: 'YYMMDD',
      kind: 'allday' as const,
      dateValue: () => toYYMMDD(nextWeekday(new Date(), dow)),
      inline: true,
      keywords: `date ${short} weekday`,
    })),
  ];

  const _SLASH_COMMANDS: SlashCommand[] = [
    // Highest-frequency commands first so they fall inside the first
    // maxRenderedOptions window at a bare `/` (ahead of the 20+ Mermaid types).
    {
      name: 'task',
      label: 'Task',
      hint: '- [ ]',
      insert: '- [ ] ‸',
      seedDateGhost: true,
      keywords: 'todo checkbox check',
    },
    {
      name: 'today',
      label: "Today's date",
      hint: 'YYMMDD',
      kind: 'allday',
      dateValue: () => getFormattedDate(),
      inline: true,
      keywords: 'date',
    },
    {
      name: 'now',
      label: 'Current time',
      hint: 'HHMM',
      kind: 'now',
      inline: true,
      keywords: 'time',
    },
    {
      name: 'schedule',
      label: 'Event',
      hint: '🗓️ event',
      keywords: 'schedule event calendar appointment due deadline',
      // Appends `> YYMMDD HHMM HHMM` at the line END (caret before it) via the
      // shared insertEvent leaf — the same behaviour as the mobile event button.
      inline: true,
      insertsEvent: true,
    },
    {
      name: 'attach',
      label: 'Attach file…',
      hint: '📎',
      insert: '',
      opensFilePicker: true,
      keywords: 'file image photo upload',
    },
    {
      name: 'bullet',
      label: 'Bullet',
      hint: '-',
      insert: '- ‸',
      seedDateGhost: true,
      keywords: 'list ul',
    },
    {
      name: 'numbered',
      label: 'Numbered list',
      hint: '1.',
      insert: '1. ‸',
      seedDateGhost: true,
      keywords: 'ol list',
    },
    { name: 'quote', label: 'Blockquote', hint: '>', insert: '> ‸', seedDateGhost: true },
    { name: 'h1', label: 'Heading 1', hint: '#', insert: '# ‸' },
    { name: 'h2', label: 'Heading 2', hint: '##', insert: '## ‸' },
    { name: 'h3', label: 'Heading 3', hint: '###', insert: '### ‸' },
    // Two-stop snippet: type the language, Tab jumps the caret into the body.
    {
      name: 'code',
      label: 'Code block',
      hint: '``` … ```',
      keywords: 'snippet fence',
      // biome-ignore lint/suspicious/noTemplateCurlyInString: ${lang}/${body} are literal snippet placeholder tokens, not a JS template interpolation.
      snippet: '```${lang}\n${body}\n```',
    },
    { name: 'table', label: 'Table', hint: '▦ 2×2', insert: '', opensTableSkeleton: true },
    // Inline `$…$` — this renders live in the editor, and reads naturally
    // mid-sentence, so it's inline-eligible.
    { name: 'math', label: 'Math', hint: '$ … $', insert: '$‸$', inline: true },
    // Display/block math (#30) — a ```math fence renders live via MathWidget (a
    // canonical multi-line `$$…$$` block is recognised too). Block-only, like
    // /code; the caret (‸) lands in the fence body.
    {
      name: 'mathblock',
      label: 'Math block',
      hint: '$$ … $$ · display',
      keywords: 'math display block equation latex',
      insert: '```math\n‸\n```\n',
    },
    // Two-stop snippet: type the link text, Tab jumps the caret into the URL.
    {
      name: 'link',
      label: 'Markdown link',
      hint: '[ ]( )',
      // biome-ignore lint/suspicious/noTemplateCurlyInString: ${text}/${url} are literal snippet placeholder tokens, not a JS template interpolation.
      snippet: '[${text}](${url})',
      inline: true,
    },
    { name: 'wiki', label: 'Wiki link', hint: '[[ ]]', insert: '[[‸]]', inline: true },
    {
      name: 'hr',
      label: 'Divider',
      hint: '---',
      insert: '---\n‸',
      keywords: 'divider rule separator',
    },
    {
      name: 'formula',
      label: 'Formula column',
      hint: 'ƒ computed',
      insert: '',
      opensFormulaSkeleton: true,
    },
    {
      name: 'footnote',
      label: 'Footnote',
      hint: '[^n]',
      keywords: 'footnote reference cite',
      inline: true,
      insertsFootnote: true,
    },
    ..._RELATIVE_DATE_COMMANDS,
    ..._MERMAID_COMMANDS,
  ];

  // Note-name suggestions (existing + referenced-but-uncreated) come from the
  // shared note-name source; wire its cache-invalidation side-effects now that
  // NoteEditor + window.NoteStorage exist.
  installNoteNameCacheInvalidation();

  // ── 1. Wiki-link source: [[partial ─────────────────────────────────────
  async function wikiCompletion(context: any): Promise<any> {
    const m = context.matchBefore(/\[\[[^\]\n]*/);
    if (!m) return null;
    const partial = m.text.slice(2).toLowerCase();
    if (!context.explicit && !partial && m.text === '[[') {
      // Don't autocomplete on bare [[ until typing starts — feels less noisy.
      // matchBefore already gives us the bracket pair, but explicit invocation
      // (Ctrl-Space) still surfaces the full list via the !context.explicit gate.
      return null;
    }
    const { existing, wanted, existingSet } = await candidateNoteNames(partial);
    const all = [...existing, ...wanted];
    if (all.length === 0) return null;
    const optFrom = m.from + 2; // start of the partial, after [[
    return {
      from: optFrom,
      to: m.to,
      validFor: /^[^\]\n]*$/,
      options: all.map((name) => ({
        label: name,
        type: existingSet.has(name) ? 'note' : 'note-new',
        detail: existingSet.has(name) ? '' : 'new',
        apply: (view: any, _completion: any, from: any, to: any) => {
          // If `]]` already follows, just replace the partial; otherwise
          // append the closing brackets too. Cursor lands past the `]]`.
          const hasClose = view.state.doc.sliceString(to, to + 2) === ']]';
          const insert = hasClose ? name : `${name}]]`;
          view.dispatch({
            changes: { from, to, insert },
            selection: { anchor: from + name.length + 2 },
          });
        },
      })),
    };
  }

  // ── 2. Markdown-link source: [text](partial ────────────────────────────
  async function mdLinkCompletion(context: any): Promise<any> {
    const m = context.matchBefore(/\[[^[\]\n]*\]\([^)\n]*/);
    if (!m) return null;
    const parenIdx = m.text.lastIndexOf('](');
    if (parenIdx === -1) return null;
    // Read the partial tolerantly: legacy underscores map to spaces, and a
    // leading `<` the user may have typed for the angle-bracket form is dropped.
    const partial = m.text
      .slice(parenIdx + 2)
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/^</, '');
    const { existing, wanted, existingSet } = await candidateNoteNames(partial);
    const all = [...existing, ...wanted];
    if (all.length === 0) return null;
    const optFrom = m.from + parenIdx + 2;
    return {
      from: optFrom,
      to: m.to,
      validFor: /^[^)\n]*$/,
      options: all.map((name) => {
        // Emit the canonical md-link target: the legacy underscore-encoded form
        // (`My Notes` → `My_Notes`); linkTarget is the single encode site.
        // Resolution stays angle-bracket-tolerant for any pre-existing links.
        const inserted = linkTarget(name);
        return {
          label: inserted,
          displayLabel: name,
          type: existingSet.has(name) ? 'note' : 'note-new',
          detail: existingSet.has(name) ? '' : 'new',
          apply: (view: any, _completion: any, from: any, to: any) => {
            const after = view.state.doc.sliceString(to, to + 1);
            const closing = after === ')' ? '' : ')';
            view.dispatch({
              changes: { from, to, insert: inserted + closing },
              selection: { anchor: from + inserted.length + closing.length },
            });
          },
        };
      }),
    };
  }

  // ── 3. Slash-command source: /partial at the start of a line, OR mid-line
  // (after a space) for inline-eligible commands ────────────────────────────
  function slashCompletion(context: any): any {
    // A `/partial` trailing the cursor, where the `/` sits at the start of the
    // line or just after whitespace — so a `/` inside a word (and/or, TCP/IP,
    // http://) never triggers. matchBefore can't anchor like this, so check the
    // leading context manually.
    const lineStart = context.state.doc.lineAt(context.pos).from;
    const beforeCursor = context.state.doc.sliceString(lineStart, context.pos);
    const m = /(?:^|\s)\/([a-z0-9]*)$/i.exec(beforeCursor);
    if (!m) return null;
    const partial = m[1].toLowerCase();
    const slashPos = context.pos - partial.length - 1;
    // Only whitespace before the `/` ⇒ start of line (offer all commands);
    // otherwise we're mid-sentence and only inline-eligible commands apply.
    const atLineStart = /^\s*$/.test(context.state.doc.sliceString(lineStart, slashPos));
    // True when any whitespace-separated keyword *starts with* the partial.
    const kwMatch = (kw: string | undefined) =>
      !!kw && kw.split(/\s+/).some((w) => w.startsWith(partial));
    let matches = _SLASH_COMMANDS.filter((c) => {
      // Mid-line: block-level inserts (headings, lists, code, table, mermaid…)
      // don't make sense, so only inline-eligible commands show.
      if (!atLineStart && !c.inline) return false;
      if (c.expandMermaid) {
        // The "Mermaid diagram…" parent shows in the bare list and for
        // diagram-ish keywords, but NOT once the user has typed a prefix of
        // "mermaid" (which reveals every subtype directly — the parent would be
        // a redundant duplicate then).
        if (!partial) return true;
        if ('mermaid'.startsWith(partial)) return false;
        return c.name.startsWith(partial) || kwMatch(c.keywords);
      }
      if (c.mermaid) {
        // Per-type Mermaid entries stay out of the bare `/` list. They surface
        // once the user types: `/mermaid` (or `/m`, `/me`, …) lists them all,
        // `/seq` `/gantt` `/pie` jump to one by name, and `/chart` `/diagram`
        // etc. match via keywords.
        if (!partial) return false;
        return 'mermaid'.startsWith(partial) || c.name.startsWith(partial) || kwMatch(c.keywords);
      }
      if (!partial) return true;
      return (
        c.name.startsWith(partial) || c.label.toLowerCase().includes(partial) || kwMatch(c.keywords)
      );
    });
    // Rank a command whose NAME starts with the partial above one that only
    // matched via its label/keywords, so `/link` selects `link` (markdown link)
    // rather than `wiki` (label "Wiki link"). Stable, so catalogue order is kept
    // within each tier (and for the bare `/` list, where every name matches "").
    matches.sort((a, b) => Number(b.name.startsWith(partial)) - Number(a.name.startsWith(partial)));
    // Typo-tolerant fallback: only when the strict pass found NOTHING and the
    // user typed ≥3 chars (avoids noisy 1-2 char over-matching). Excludes the
    // Mermaid parent + gated subtypes so a typo can't resurface the diagram
    // list, and keeps the strict-tier ranking untouched for every real match.
    if (matches.length === 0 && partial.length >= 3) {
      matches = _SLASH_COMMANDS.filter(
        (c) => !c.mermaid && !c.expandMermaid && (atLineStart || c.inline) && _fuzzyCmd(partial, c)
      );
      matches.sort(
        (a, b) => Number(b.name.startsWith(partial)) - Number(a.name.startsWith(partial))
      );
    }
    if (matches.length === 0) return null;
    return {
      from: slashPos,
      to: context.pos,
      // We filter the catalogue ourselves (above); tell CM6 not to re-filter the
      // options against each one's `/name` label — otherwise typing "mermaid"
      // would only match the parent and hide the per-type entries. With
      // `filter: false` we must also OMIT `validFor`: validFor would let CM6 keep
      // the popup's first option set and skip re-querying, so the per-keystroke
      // narrowing (and the Mermaid reveal) would never run. Without it CM6
      // re-invokes this source on every change; it returns null once the line
      // stops matching `/…`, which closes the menu just as validFor used to.
      filter: false,
      options: matches.map((cmd) => ({
        label: `/${cmd.name}`,
        displayLabel: cmd.label,
        detail: cmd.hint,
        type: 'keyword',
        apply: (view: any, _completion: any, from: any, to: any) => {
          // Context-aware date commands (/today, /now, relative dates): promote
          // an end-of-line to a schedule line, upgrade an all-day tail to timed,
          // or insert a bare date — see _applyAllDayDate / _applyNow.
          if (cmd.kind === 'allday') {
            _applyAllDayDate(view, from, to, cmd.dateValue ? cmd.dateValue() : getFormattedDate());
            return;
          }
          if (cmd.kind === 'now') {
            _applyNow(view, from, to);
            return;
          }
          // /schedule (and the mobile event button) share this leaf: append the
          // timed stamp at the line end with the caret before it.
          if (cmd.insertsEvent) {
            insertEvent(view, from, to);
            return;
          }
          // /footnote: auto-numbered ref at the caret + definition stub at doc-end.
          if (cmd.insertsFootnote) {
            _applyFootnote(view, from, to);
            return;
          }
          // "Mermaid diagram…" parent: rewrite the token to `/mermaid` and
          // re-open the palette listing every subtype.
          if (cmd.expandMermaid) {
            view.dispatch({
              changes: { from, to, insert: '/mermaid' },
              selection: { anchor: from + '/mermaid'.length },
            });
            queueMicrotask(() => NoteEditor.triggerCompletion?.());
            return;
          }
          if (cmd.opensTableSkeleton && typeof NoteEditor.insertTableSkeleton === 'function') {
            // Skeleton insertion owns the dispatch (wipes the `/table` text,
            // adds a leading blank line if needed, queues first-cell focus).
            NoteEditor.insertTableSkeleton(from, to);
            return;
          }
          if (cmd.opensFormulaSkeleton && typeof NoteEditor.insertTableSkeleton === 'function') {
            // A table with a computed (formula) column, to teach the
            // `$name = expr$` header syntax — caret lands in the formula header.
            NoteEditor.insertTableSkeleton(from, to, {
              template: '| Qty | Price | $Total = Qty * Price$ |\n| - | - | - |\n|  |  |  |',
              focus: { row: -1, col: 2 },
            });
            return;
          }
          if (cmd.snippet) {
            // CM6 snippet with ${field} tab-stops (e.g. /link). The snippet
            // apply self-installs the snippet field + theme via appendConfig;
            // Tab/Shift-Tab between fields is wired in note-editor's keymap.
            const _snippet = (window as any).CM6?.autocomplete?.snippet;
            if (typeof _snippet === 'function') {
              _snippet(cmd.snippet)(view, _completion, from, to);
            } else {
              // Fallback (no CM6 global, e.g. headless tests): insert the
              // template with the ${field} names as plain placeholder text.
              const text = cmd.snippet.replace(/\$\{([^}]*)\}/g, '$1');
              view.dispatch({ changes: { from, to, insert: text }, selection: { anchor: from } });
            }
            return;
          }
          // `build()` computes the insert fresh at apply time (current date/time);
          // otherwise use the static `insert` template.
          const tpl = cmd.build ? cmd.build() : cmd.insert || '';
          const sentinelIdx = tpl.indexOf('‸');
          const expanded = sentinelIdx === -1 ? tpl : tpl.replace('‸', '');
          const caretPos = from + (sentinelIdx === -1 ? expanded.length : sentinelIdx);
          view.dispatch({
            changes: { from, to, insert: expanded },
            selection: { anchor: caretPos },
          });
          if (cmd.seedDateGhost && typeof NoteEditor.seedGhost === 'function') {
            // Trailing all-day schedule date the user can Tab to append after the
            // task text, turning it into a dated task: "- [ ] buy milk > 260610".
            // The `> ` marker is REQUIRED for the date to register as a schedule
            // (a bare date is just plain text); the task body is the description
            // that precedes the `>`. A chained `next` offers a SECOND Tab that
            // appends the now→+1h times, turning it into a timed item. queueMicro-
            // task (not rAF) so it still fires when the tab isn't focused — rAF is
            // paused in background tabs — and runs right after the completion-
            // accept/popup-close settle.
            queueMicrotask(() =>
              NoteEditor.seedGhost(` > ${_slashToday()}`, { text: ` ${nowTimeRange()}` })
            );
          }
          if (cmd.opensFilePicker && AttachmentPicker?.pick) {
            requestAnimationFrame(() => AttachmentPicker.pick());
          }
        },
      })),
    };
  }

  // ── 4. New-note title source: # partial on first line of unsaved note ──
  // Selecting an existing name LOADS that note instead of inserting text.
  async function newNoteCompletion(context: any): Promise<any> {
    if (typeof getCurrentFileName() !== 'undefined' && getCurrentFileName() !== null) return null;
    const headLine = context.state.doc.line(1);
    if (context.pos < headLine.from || context.pos > headLine.to) return null;
    const m = /^(#\s+)(.*)$/.exec(headLine.text);
    if (!m) return null;
    const titleStart = headLine.from + m[1].length;
    if (context.pos < titleStart) return null;
    const partial = headLine.text.slice(m[1].length, context.pos - headLine.from).toLowerCase();
    const VIRTUAL = new Set([LABYRINTH_NOTE, CALENDARS_NOTE]);
    const names = await window.NoteStorage.getAllNoteNames();
    const filtered = names
      .filter((n: any) => !n.startsWith('.') && !VIRTUAL.has(n))
      .filter((n: any) => !partial || n.toLowerCase().includes(partial))
      .sort((a: any, b: any) => {
        if (!partial) return b.localeCompare(a);
        const as = a.toLowerCase().startsWith(partial);
        const bs = b.toLowerCase().startsWith(partial);
        return Number(bs) - Number(as) || a.localeCompare(b);
      })
      .slice(0, 10);
    if (filtered.length === 0) return null;
    return {
      from: titleStart,
      to: context.pos,
      validFor: /^[^\n]*$/,
      options: filtered.map((name: any) => ({
        label: name,
        type: 'note',
        detail: 'open',
        apply: (view: any, _completion: any, from: any, to: any) => {
          // Existing note → load it. Non-matching typed text reaches this
          // path only via explicit acceptance, so fall back to inserting
          // the literal name when the note no longer exists by the time
          // apply fires (race with concurrent deletion).
          window.NoteStorage.getNote(name).then((content: any) => {
            if (content !== null) {
              loadNote(name);
            } else {
              view.dispatch({
                changes: { from, to, insert: name },
                selection: { anchor: from + name.length },
              });
            }
          });
        },
      })),
    };
  }

  // ── 5. Math source: `\command` + column-reducer completion inside math ──
  // Fires ONLY inside a math context — an inline `$…$` (odd-parity scan of the
  // line up to the cursor, `$$` collapsed to one toggle, `\$` ignored) or a
  // ```math fence (syntaxTree FencedCode whose info string is `math`). A `$`
  // inside inline code / a non-math fence is literal, so those bail. Out of
  // context it returns null (closing the menu).
  const _REDUCER_PAREN_RE = new RegExp(`\\b(?:${VEC_FN_NAMES.join('|')})\\s*\\([^()]*$`);

  // In a math context (an inline `$…$` or a ```math fence) — delegated to the
  // shared `mathContextAtCaret` leaf so the completion source, Tab field-nav, and
  // Tab `=`-open all agree on what counts as "in math".
  function _inMathContext(context: any): boolean {
    return !!mathContextAtCaret(context.state, context.pos);
  }

  // Apply a CM6 snippet (via the global `snippet()`), degrading to plain text
  // with the `${n}` tab-stops stripped when the CM6 global is absent (headless).
  function _applyMathSnippet(snippet: string) {
    return (view: any, _completion: any, from: any, to: any) => {
      const _snippet = (window as any).CM6?.autocomplete?.snippet;
      if (typeof _snippet === 'function') {
        _snippet(snippet)(view, _completion, from, to);
      } else {
        const text = snippet.replace(/\$\{[^}]*\}/g, '');
        view.dispatch({
          changes: { from, to, insert: text },
          selection: { anchor: from + text.length },
        });
      }
    };
  }

  function mathCompletion(context: any): any {
    if (!_inMathContext(context)) return null;
    // `\command` — offer the single-sourced LaTeX vocabulary. CM6 filters the
    // returned options against the typed `\fra…` via validFor.
    const bs = context.matchBefore(/\\[a-zA-Z]*/);
    if (bs) {
      return {
        from: bs.from,
        to: context.pos,
        validFor: /^\\[a-zA-Z]*$/,
        options: MATH_LATEX_COMMANDS.map((c) => ({
          label: `\\${c.cmd}`,
          detail: c.detail,
          type: 'function',
          apply: _applyMathSnippet(c.snippet),
        })),
      };
    }
    // Bare identifier — offer the column reducers (sum/mean/…) always, plus the
    // note's ACTUAL column names when the cursor sits inside a reducer's parens
    // (`sum(Pr…`), sourced live from the editor's column env.
    const id = context.matchBefore(/[a-zA-Z][a-zA-Z0-9]*/);
    if (!id) return null;
    const options: any[] = VEC_FN_NAMES.map((name) => ({
      label: name,
      detail: 'column reducer',
      type: 'function',
      // `\${1}` is a CM6 snippet tab-stop (escaped so it stays literal here).
      apply: _applyMathSnippet(`${name}(\${1})`),
    }));
    const lineStart = context.state.doc.lineAt(context.pos).from;
    const before = context.state.doc.sliceString(lineStart, id.from);
    if (_REDUCER_PAREN_RE.test(before)) {
      const env = NoteEditor.getColumnMathEnv?.();
      if (env) {
        for (const colName of Object.keys(env)) {
          options.push({ label: colName, detail: 'column', type: 'variable' });
        }
      }
    }
    return { from: id.from, to: context.pos, validFor: /^[a-zA-Z][a-zA-Z0-9]*$/, options };
  }

  // Register sources in priority order. CM6 walks the override list and
  // returns the first non-null result; matching priority is therefore set
  // by registration order. The math source is registered last (it self-gates on
  // math context, so nothing else needs to yield to it).
  NoteEditor.registerCompletionSource(slashCompletion);
  NoteEditor.registerCompletionSource(wikiCompletion);
  NoteEditor.registerCompletionSource(mdLinkCompletion);
  NoteEditor.registerCompletionSource(newNoteCompletion);
  NoteEditor.registerCompletionSource(mathCompletion);

  // Public API: newNote() calls this immediately after creating a fresh
  // note so the title autocomplete pops without the user typing.
  window.Autocomplete = {
    triggerNewNoteDropdown() {
      if (typeof getCurrentFileName() !== 'undefined' && getCurrentFileName() !== null) return;
      NoteEditor.triggerCompletion();
    },
  };
})();

// Phase 4.5: ES-module export. The IIFE above keeps the
// window.Autocomplete back-compat shim until 4.7.
export const Autocomplete = window.Autocomplete;
